import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import classNames from "classnames";
import LivingMap, {
  LMFeature,
  LivingMapOptions,
} from "@livingmap/core-mapping";
import {
  FloatingIconButton,
  Icon,
  Spinner,
  Tooltip,
  ZoomControl,
} from "@livingmap/core-ui-v2";

import {
  Button,
  Dialog,
  FloatingIconButton as HippoFloatingIconButton,
  IconButton,
  TextButton,
  TextCard,
  MapControlFloorSelector,
} from "@livingmap/hippo";
import { Sheet } from "../Sheet";

import {
  Bookmark,
  Geofence,
  LanguageConfig,
  MappedBookmark,
  MappedFloor,
  Region,
} from "../../redux/services/types";
import useLocationPermission from "../../hooks/useLocationPermission";
import useMotionAndOrientationPermission from "../../hooks/useMotionAndOrientationPermission";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setLanguage,
  setTriggeredGeofenceId,
  storeLocation,
} from "../../redux/slices/applicationSlice";
import useResponsive from "../../hooks/useResponsive";
import { usePostAnalyticsEvent } from "../../hooks/usePostAnalyticsEvent";
import { useBookmarkData } from "../../hooks/useBookmarkData";

import FloorControl from "./plugins/floor-control";
import ClusteredPinPlugin from "./plugins/clustered-pin-control";
import InteractionPlugin from "./plugins/interaction-control";
import { PLUGIN_IDS } from "./plugins/types/index";
import PositionPlugin from "./plugins/position-control";
import LayerIconPlugin from "./plugins/layer-icon-control";
import RoutingPlugin from "./plugins/routing-control";
import RegionPlugin from "./plugins/regions-control";
import GeofencePlugin from "./plugins/geofence-control";

import { buildRoutingPath } from "../../utils/buildRoutingPath";
import { push, replace } from "../../utils/navigate";
import { Path, RoutingPath } from "../../utils/types";
import { AnalyticsEvent } from "../../utils/analyticsTypes";
import { parseLanguageObject } from "../../utils/parseLanguageObject";

import CompassButton from "../CompassButton/CompassButton";
import LocationButton, {
  LocationStatus,
} from "../LocationButton/LocationButton";

import { setLocationStatus } from "../../redux/slices/applicationSlice";

import "mapbox-gl/dist/mapbox-gl.css";
import styles from "./Map.module.scss";
import DebugControl from "./plugins/debug-control";
import { useTranslation } from "react-i18next";
import i18n from "../../i18next";
import Advert from "../Advert/Advert";
import markerSDK from "@marker.io/browser";

export interface MapCentre {
  lat: number;
  lng: number;
}

interface Props {
  dataQA: string;
  mapID: string;
  zoom: number;
  maxZoom: number;
  minZoom: number;
  center: [number, number];
  extent: [number, number, number, number];
  bearing: number;
  mapStyle: string;
  accessToken: string;
  markerIoProjectId?: string | null;
  floor: MappedFloor;
  floors: MappedFloor[];
  regions: Region[];
  rawBookmarks: Bookmark[];
  attributionHTML: string;
  onFeatureSelect?: (feature: LMFeature) => void;
  onMapReady?: (map: LivingMap) => void;
  onFloorChange?: (floor: MappedFloor) => void;
  enableUI?: boolean;
  enableUserLocation?: boolean;
  mapInstanceRef: React.MutableRefObject<LivingMap | null>;
  positionOnRoute: boolean;
  availableLanguages: LanguageConfig[];
  geofences?: Geofence[];
  searchTags?: boolean;
}

const Map: React.FC<Props> = ({
  dataQA,
  mapID,
  bearing,
  center,
  extent,
  maxZoom,
  minZoom,
  zoom,
  mapStyle,
  accessToken,
  markerIoProjectId,
  floor,
  floors,
  regions,
  rawBookmarks,
  attributionHTML,
  onFeatureSelect,
  onMapReady,
  enableUI = true,
  enableUserLocation = true,
  onFloorChange,
  mapInstanceRef,
  positionOnRoute,
  availableLanguages,
  geofences,
  searchTags,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  // Map state
  const [isLoading, setIsLoading] = useState(false);
  const [isLocationErrorDialogOpen, setIsLocationErrorDialogOpen] =
    useState(false);
  const [locationErrorBody, setLocationErrorBody] = useState("");
  const [isMapAttributionOpen, setIsMapAttributionOpen] = useState(false);
  const [liveBearing, setLiveBearing] = useState(0);
  const [liveZoom, setLiveZoom] = useState(0);
  const [liveFloors, setLiveFloors] = useState<MappedFloor[]>([]);
  const [routeGeofences, setRouteGeofences] = useState<Geofence[]>([]);
  const [selectedRouteGeofence, setSelectedRouteGeofence] =
    useState<JSX.Element>();
  const [locationRequestSuccessful, setLocationRequestSuccessful] =
    useState(false);

  const isOnJourneyScreen = useMatch(Path.JOURNEY);
  const isOnHomeScreen = useMatch(Path.HOME);

  // Get location status and coords from redux
  const {
    locationStatus,
    queryParamsConfig,
    language,
    triggeredGeofenceId,
    defaultLanguage,
  } = useAppSelector((state) => state.application);

  const triggeredGeofence = useMemo(
    () => geofences?.find((geofence) => geofence.uuid === triggeredGeofenceId),
    [geofences, triggeredGeofenceId],
  );

  const { logAnalyticsEvent } = usePostAnalyticsEvent();
  const { bookmarks } = useBookmarkData(rawBookmarks);

  const handleLocationButtonClick = () => {
    const position = positionControlInstance.current?.getCurrentPosition();
    if (locationStatus === LocationStatus.FOUND && position)
      mapInstanceRef.current?.getMapboxMap().easeTo({
        center: [position.longitude, position.latitude],
      });
    else if (locationStatus !== LocationStatus.DISABLED) {
      // Only attempt location request if permissions aren't disabled
      handleLocationRequest();

      // Only reset stabilisation if we're actually going to request location
      if (locationStatus === LocationStatus.INACTIVE)
        positionControlInstance.current?.resetStabilisation();
    } else if (locationStatus === LocationStatus.DISABLED) {
      setLocationErrorBody(t("home.user_location_declined_alert"));
      setIsLocationErrorDialogOpen(true);
    }

    requestMotionAndOrientationPermission();
  };

  const { requestMotionAndOrientationPermission } =
    useMotionAndOrientationPermission();

  const { Mobile } = useResponsive();

  // Container for the map
  const mapContainer = useRef<HTMLDivElement | null>(null);
  // Ref for the map instance so we can access it when we want to remove it
  const mapRef = useRef<LivingMap | null>(null);

  // Map Plugins
  const clusteredPinControlInstance = useRef<ClusteredPinPlugin | null>(null);
  const floorControlInstance = useRef<FloorControl | null>(null);
  const interactionControlInstance = useRef<InteractionPlugin | null>(null);
  const positionControlInstance = useRef<PositionPlugin | null>(null);
  const layerIconControlInstance = useRef<LayerIconPlugin | null>(null);
  const routingControlInstance = useRef<RoutingPlugin | null>(null);
  const regionControlInstance = useRef<RegionPlugin | null>(null);
  const debugControlInstance = useRef<DebugControl | null>(null);
  const geofenceControlInstance = useRef<GeofencePlugin | null>(null);

  // Location permission hook
  const { handleLocationRequest } = useLocationPermission({
    project: mapID,
    floorControl: floorControlInstance.current,
    positionControl: positionControlInstance.current,
    geofenceControl: geofenceControlInstance.current,
  });

  //i18next translation hook
  const { t } = useTranslation();

  // Language select sheet state
  const [languageSelectSheetOpen, setLanguageSelectSheetOpen] = useState(false);
  const [languageSheetContentNode, setLanguageSheetContentNode] =
    useState<HTMLUListElement | null>(null);
  const [languageSheetContentHeight, setLanguageSheetContentHeight] = useState<
    number | null
  >(null);

  useLayoutEffect(() => {
    if (!languageSelectSheetOpen) {
      setLanguageSheetContentHeight(null);
      return;
    }

    if (!languageSheetContentNode) return;

    const contentElement = languageSheetContentNode;

    const measureContent = () => {
      const measuredElement = contentElement.parentElement ?? contentElement;
      const height = measuredElement.scrollHeight;
      if (height > 0) {
        setLanguageSheetContentHeight(height);
        return true;
      }
      return false;
    };

    if (measureContent()) return;

    if (typeof ResizeObserver === "undefined") {
      const frame = requestAnimationFrame(() => {
        measureContent();
      });

      return () => cancelAnimationFrame(frame);
    }

    const observer = new ResizeObserver(() => {
      if (measureContent()) {
        observer.disconnect();
      }
    });

    observer.observe(contentElement);

    return () => observer.disconnect();
  }, [
    languageSelectSheetOpen,
    languageSheetContentNode,
    availableLanguages.length,
  ]);

  // Ease the map to the user location when we have locationRequestSuccessful is currently false
  useEffect(() => {
    const position = positionControlInstance.current?.getCurrentPosition();
    if (!locationRequestSuccessful && position) {
      setLocationRequestSuccessful(true);
      mapInstanceRef.current?.getMapboxMap().easeTo({
        center: [position.longitude, position.latitude],
      });
    }
    if (locationStatus === LocationStatus.DISABLED) {
      setLocationErrorBody(t("home.user_location_declined_alert"));
      setIsLocationErrorDialogOpen(true);
    }
  }, [locationStatus, locationRequestSuccessful, mapInstanceRef, t]);

  useEffect(() => {
    if (locationStatus === LocationStatus.FOUND)
      logAnalyticsEvent({ event_type: AnalyticsEvent.SHOW_POSITION_ON });
    else if (locationStatus === LocationStatus.OUTOFBOUNDS) {
      setLocationErrorBody(t("home.user_location_out_of_bounds"));
      setIsLocationErrorDialogOpen(true);
      positionControlInstance.current?.stopStabilisation();
      dispatch(setLocationStatus(LocationStatus.INACTIVE));
    }
  }, [locationStatus, logAnalyticsEvent, t, dispatch]);

  // Map initialisation
  useEffect(() => {
    if (!mapID || !mapContainer.current) return;

    setIsLoading(true);

    // Store old map before creating new one so we can remove it after the new one has loaded
    const oldMap = mapRef.current;

    const mapConfig: LivingMapOptions = {
      accessToken,
      zoom,
      maxZoom,
      minZoom,
      center,
      extent,
      bearing,
      style: mapStyle,
      hash: true,
      enablePitchWithRotate: true,
      enableTouchPitch: false,
      preserveDrawingBuffer: true,
      bearingSnap: 0,
    };

    const map = new LivingMap(mapContainer.current, mapConfig);
    mapRef.current = map;

    floorControlInstance.current = new FloorControl(PLUGIN_IDS.FLOOR, map);

    clusteredPinControlInstance.current = new ClusteredPinPlugin(
      PLUGIN_IDS.CLUSTERED_PIN,
      map,
      floorControlInstance.current,
    );

    routingControlInstance.current = new RoutingPlugin(
      PLUGIN_IDS.ROUTING,
      map,
      floors,
      onFloorChange,
      setRouteGeofences,
    );

    interactionControlInstance.current = new InteractionPlugin(
      PLUGIN_IDS.INTERACTION,
      map,
      {
        debug: queryParamsConfig.debug === "enable",
      },
      routingControlInstance.current,
    );

    positionControlInstance.current = new PositionPlugin(
      PLUGIN_IDS.USER_LOCATION,
      map,
      positionOnRoute,
      routingControlInstance.current,
      queryParamsConfig.debug === "enable",
      // onStabilisationSuccess
      () => {
        dispatch(setLocationStatus(LocationStatus.FOUND));
      },
      // onStabilisationFailure
      (log: string) => {
        if (queryParamsConfig.debug === "enable") console.info(`❌ ${log}`);
        setLocationErrorBody(t("location_error_dialog.body"));
        setIsLocationErrorDialogOpen(true);
        dispatch(setLocationStatus(LocationStatus.INACTIVE));
      },
    );

    layerIconControlInstance.current = new LayerIconPlugin(
      PLUGIN_IDS.LAYER_ICON,
      map,
    );

    regionControlInstance.current = new RegionPlugin(
      PLUGIN_IDS.REGION,
      map,
      regions,
      floors,
    );

    debugControlInstance.current = new DebugControl(PLUGIN_IDS.DEBUG, map);

    geofenceControlInstance.current = new GeofencePlugin(
      PLUGIN_IDS.GEOFENCE,
      map,
      floorControlInstance.current,
      (geofenceId) => dispatch(setTriggeredGeofenceId(geofenceId)),
    );

    map.addPlugin(floorControlInstance.current);
    map.addPlugin(clusteredPinControlInstance.current);
    map.addPlugin(interactionControlInstance.current);
    map.addPlugin(positionControlInstance.current);
    map.addPlugin(layerIconControlInstance.current);
    map.addPlugin(routingControlInstance.current);
    map.addPlugin(regionControlInstance.current);
    map.addPlugin(debugControlInstance.current);
    map.addPlugin(geofenceControlInstance.current);

    map.create();

    map.on("style.load", async () => {
      floorControlInstance.current?.setGroundFloor(floors);
      floorControlInstance.current?.setAllFloors(floors);
      floorControlInstance.current?.setActiveFloor(floor);

      if (queryParamsConfig.debug === "enable")
        debugControlInstance.current?.setRegions(regions);

      if (geofences) {
        geofenceControlInstance.current?.setGeofences(geofences);
        routingControlInstance.current?.setGeofences(geofences);
        if (queryParamsConfig.debug === "enable")
          debugControlInstance.current?.setGeofences(geofences);
      }

      if (onFeatureSelect) {
        interactionControlInstance.current?.subscribeToFeatureSelect(
          onFeatureSelect,
        );
      }

      // Initialise map instance and state values
      mapInstanceRef.current = map;

      setLiveZoom(zoom);
      map.getMapboxMap().setMaxPitch(0);
      if (map.getMapboxMap().getBearing() === 0) {
        map.getMapboxMap().setBearing(bearing);
        setLiveBearing(bearing);
      } else {
        setLiveBearing(map.getMapboxMap().getBearing());
      }

      if (!regionControlInstance.current) {
        setLiveFloors(floors);
      } else {
        setLiveFloors(regionControlInstance.current.getAvailableFloors());
      }

      onMapReady && onMapReady(map);

      if (!markerIoProjectId) return;

      const markerIoInstance = await markerSDK.loadWidget({
        project: markerIoProjectId,
      });

      markerIoInstance.show();
    });

    map.on("render", function () {
      map?.getMapboxMap().resize();
    });

    map.on("load", () => {
      // Add the instance of LivingMap to the window object to assist with testing
      if (process.env.REACT_APP_STAGING === "true") {
        window.livingMap = map;
        console.log(
          "LivingMap instance injected into window.livingMap successfully",
        );
      }

      if (queryParamsConfig["ui-gestures"] === "disable") {
        map.getMapboxMap().scrollZoom.disable();
        map.getMapboxMap().dragPan.disable();
        map.getMapboxMap().doubleClickZoom.disable();
        map.getMapboxMap().touchZoomRotate.disable();
      }

      if (queryParamsConfig.consoleLocation === "enable") {
        window.api = {
          ...window.api,
          getCenter: () => map.getMapboxMap().getCenter(),
        };
      }

      logAnalyticsEvent({
        event_type: AnalyticsEvent.LANGUAGE_CHANGE,
        event_data: language,
      });

      logAnalyticsEvent({
        event_type: AnalyticsEvent.MAP_LOAD,
        event_data: {
          type: window !== window.parent ? "embedded" : "native",
          ...(queryParamsConfig.source && { source: queryParamsConfig.source }),
          device_height: window.innerHeight,
          device_width: window.innerWidth,
        },
      });

      handleRegionUpdate();

      if (oldMap) oldMap.getMapboxMap().remove();

      setIsLoading(false);
    });

    map.getMapboxMap().on("rotate", () => {
      setLiveBearing(map.getMapboxMap().getBearing());
    });

    map.getMapboxMap().on("zoom", () => {
      setLiveZoom(Math.floor(map.getMapboxMap().getZoom()));
    });

    map.getMapboxMap().on("resize", () => {
      // timeout to fix flickering issue
      setTimeout(() => map.getMapboxMap().resize(), 50);
    });

    map.getMapboxMap().on("zoomend", handleRegionUpdate);
    map.getMapboxMap().on("dragend", handleRegionUpdate);
    map.getMapboxMap().on("moveend", handleRegionUpdate);

    // eslint-disable-next-line
  }, [mapID, mapStyle]); // these are the only required dependencies

  const handleRegionUpdate = () => {
    if (regionControlInstance.current) {
      regionControlInstance.current.updateAvailableFloors();
      setLiveFloors(regionControlInstance.current.getAvailableFloors());
    }
  };

  const handleMapZoomIn = () => {
    mapInstanceRef?.current?.zoomOutOneLevel();
  };

  const handleMapZoomOut = () => {
    mapInstanceRef?.current?.zoomInOneLevel();
  };

  const handleFloorSelect = (newFloor: MappedFloor) => {
    floorControlInstance.current?.setActiveFloor(newFloor);
    onFloorChange && onFloorChange(newFloor);

    logAnalyticsEvent({
      event_type: AnalyticsEvent.MAP_LEVEL_CHANGE,
      event_data: {
        from_floor: floor.floor,
        to_floor: newFloor.floor,
      },
    });
  };

  const handleBookmarkSelect = (bookmark: MappedBookmark) => {
    mapInstanceRef.current?.getMapboxMap().easeTo({
      center: [bookmark.center.longitude, bookmark.center.latitude],
      bearing: bookmark.bearing,
      zoom: bookmark.zoom,
    });

    floorControlInstance.current?.setActiveFloor(bookmark.floor);

    const rawBookmark = rawBookmarks.find(
      (rawBookmark) =>
        parseLanguageObject(rawBookmark.name, language) === bookmark.name,
    );

    logAnalyticsEvent({
      event_type: AnalyticsEvent.BOOKMARK_SELECT,
      event_data: rawBookmark
        ? parseLanguageObject(rawBookmark.name, defaultLanguage) || ""
        : bookmark.name,
    });
  };

  useEffect(() => {
    const generateAdvert = (geofence: Geofence) => {
      return (
        <Advert
          dataQA="geofence-advert"
          className={classNames(styles.routeAdvert, {
            [styles.extendedPadding]: searchTags,
          })}
          imageUrl={geofence.image_url || ""}
          newTab={geofence.new_tab}
          adUrl={geofence.ad_url}
          onClose={() => {
            setRouteGeofences([]);
            setSelectedRouteGeofence(undefined);
          }}
          title={parseLanguageObject(geofence.title, language) || ""}
          {...(geofence.subtitle
            ? {
                subtitle:
                  parseLanguageObject(geofence.subtitle, language) || "",
              }
            : {})}
          {...(geofence.text
            ? {
                text: parseLanguageObject(geofence.text, language) || "",
              }
            : {})}
        />
      );
    };

    let currentIndex = 1;
    if (routeGeofences.length > 1 && isOnJourneyScreen) {
      const interval = setInterval(() => {
        setSelectedRouteGeofence(generateAdvert(routeGeofences[currentIndex]));
        currentIndex += 1;
        if (currentIndex >= routeGeofences.length) {
          currentIndex = 0;
        }
      }, 7 * 1000);
      setSelectedRouteGeofence(generateAdvert(routeGeofences[0]));
      return () => clearInterval(interval);
    } else if (routeGeofences.length === 1 && isOnJourneyScreen) {
      setSelectedRouteGeofence(generateAdvert(routeGeofences[0]));
    }
  }, [
    isOnJourneyScreen,
    setSelectedRouteGeofence,
    routeGeofences,
    language,
    searchTags,
  ]);

  useEffect(() => {
    if (!isOnHomeScreen) dispatch(setTriggeredGeofenceId(null));
  }, [dispatch, isOnHomeScreen]);

  return (
    <div data-qa={dataQA} className={styles.container}>
      {isLoading && (
        <div className={styles.loaderContainer}>
          <Spinner dataQA="map-loading-spinner" type="BeatLoader" />
        </div>
      )}
      <div ref={mapContainer} className={styles.map} />
      {enableUI && floors.length > 1 && (
        <div className={styles.floorSelectorContainer}>
          <MapControlFloorSelector
            dataQA={"map-floor-selector"}
            buildings={[
              {
                id: 101,
                name: t("home.floor_selector_find_building"),
                floors: liveFloors,
              },
            ]}
            bookmarks={bookmarks}
            tooltip={{
              text: t("home.floor_selector_tooltip"),
              position: "left",
            }}
            anchor={Mobile ? "centre" : "end"}
            selectedFloorId={
              Object.keys(liveFloors).length ? floor.id : undefined
            }
            onSelectFloor={handleFloorSelect}
            onBookmarkSelect={handleBookmarkSelect}
            onClick={() =>
              logAnalyticsEvent({
                event_type: AnalyticsEvent.LEVEL_SELECTOR_TOUCH,
              })
            }
          />
        </div>
      )}
      {enableUI && availableLanguages.length > 1 && (
        <div className={styles.leftMobileControlsContainer}>
          <HippoFloatingIconButton
            dataQA="mobile-language-switcher"
            icon="globe"
            size="regular"
            iconSize={24}
            type="primary"
            onClick={() => setLanguageSelectSheetOpen(true)}
          />
        </div>
      )}
      {enableUI && (
        <div className={styles.mapControlsContainer}>
          <div className={styles.control}>
            <Tooltip
              dataQA="compass-tooltip"
              className={styles.tooltip}
              triggerComponent={
                <CompassButton
                  size={Mobile ? "medium" : "small"}
                  bearing={liveBearing}
                  dataQA="map-compass"
                  onClick={() => {
                    if (
                      mapInstanceRef?.current?.getMapboxMap().getBearing() === 0
                    ) {
                      mapInstanceRef?.current
                        ?.getMapboxMap()
                        .easeTo({ bearing });
                      logAnalyticsEvent({
                        event_type: AnalyticsEvent.NORTH_ORIENTATION_OFF,
                      });
                    } else {
                      mapInstanceRef?.current
                        ?.getMapboxMap()
                        .easeTo({ bearing: 0 });
                      logAnalyticsEvent({
                        event_type: AnalyticsEvent.NORTH_ORIENTATION_ON,
                      });
                    }
                  }}
                />
              }
              children={<p>{t("home.compass_tooltip")}</p>}
            />
          </div>
          <div>
            {isOnJourneyScreen && (
              <div
                className={classNames(
                  styles.control,
                  styles.journeyOverviewControl,
                )}
              >
                <FloatingIconButton
                  dataQA={"journey-overview-button"}
                  icon={"JourneyOverviewIcon"}
                  onClick={() => {
                    dispatch(storeLocation(location));

                    const { fromId, toId } = isOnJourneyScreen.params;

                    dispatch(
                      push({
                        pathOrLocation: buildRoutingPath(
                          RoutingPath.JOURNEY_OVERVIEW,
                          {
                            fromId,
                            toId,
                          },
                        ),
                      }),
                    );
                  }}
                  rounded
                  size={Mobile ? "medium" : "small"}
                />
              </div>
            )}
            {enableUserLocation && (
              <div className={classNames(styles.control)}>
                <Tooltip
                  dataQA="user-location-tooltip"
                  className={styles.tooltip}
                  triggerComponent={
                    <LocationButton
                      dataQA="map-user-location"
                      onClick={handleLocationButtonClick}
                      status={locationStatus}
                      size={Mobile ? "medium" : "small"}
                      colour={
                        queryParamsConfig.positionLogging === "enable"
                          ? "red"
                          : queryParamsConfig.positionLoggingSessionUUID?.length
                            ? "green"
                            : "blue"
                      }
                    />
                  }
                  children={<p>{t("home.locate_me_tooltip")}</p>}
                />
              </div>
            )}
          </div>
          <div className={styles.zoomControl}>
            <Tooltip
              dataQA="zoom-tooltip"
              className={styles.tooltip}
              triggerComponent={
                <ZoomControl
                  dataQA="map-zoom-control"
                  maxZoomReached={liveZoom >= maxZoom}
                  minZoomReached={liveZoom <= minZoom}
                  onZoomInClick={() => {
                    setLiveZoom(
                      Math.floor(
                        mapInstanceRef?.current?.getMapboxMap().getZoom()!,
                      ),
                    );
                    handleMapZoomIn();
                  }}
                  onZoomOutClick={() => {
                    setLiveZoom(
                      Math.floor(
                        mapInstanceRef?.current?.getMapboxMap().getZoom()!,
                      ),
                    );
                    handleMapZoomOut();
                  }}
                />
              }
              children={<p>{t("home.zoom_tooltip")}</p>}
            />
          </div>
        </div>
      )}
      <Icon
        dataQA="map-attribution"
        type="InfoOutlinedIcon"
        onClick={() => {
          setIsMapAttributionOpen(true);
          logAnalyticsEvent({ event_type: AnalyticsEvent.ATTRIBUTION_OPEN });
        }}
        className={styles.attribution}
      />
      {Mobile && (
        <Sheet
          dataQA="language-select-sheet"
          open={languageSelectSheetOpen}
          className={styles.languageSelectSheet}
          scrollLocking={false}
          snapPoints={({ maxHeight, headerHeight }) => {
            const contentHeight = languageSheetContentHeight ?? 0;
            const desiredHeight = headerHeight + contentHeight;
            const maxSnapPoint = Math.min(maxHeight - 20, desiredHeight);
            return [headerHeight, Math.max(headerHeight, maxSnapPoint)];
          }}
          defaultSnap={({ snapPoints }) => snapPoints[snapPoints.length - 1]}
          header={
            <div className={styles.languageSelectSheetHeader}>
              <span>Language</span>
              <IconButton
                dataQA="language-select-sheet-close-button"
                icon="close"
                iconSize={24}
                size="regular"
                type="primary"
                onClick={() => setLanguageSelectSheetOpen(false)}
              />
            </div>
          }
        >
          <ul ref={setLanguageSheetContentNode}>
            {availableLanguages.map((availableLanguage) => (
              <li key={availableLanguage.id}>
                <TextCard
                  dataQA="language-select-option"
                  title={availableLanguage.human_readable_language}
                  subtitle={availableLanguage.human_readable_region}
                  onClick={() => {
                    dispatch(setLanguage(availableLanguage.id));
                    i18n.changeLanguage(availableLanguage.id);

                    dispatch(
                      replace({
                        pathOrLocation: location.pathname,
                        newQueryParams: {
                          lang: availableLanguage.id,
                        },
                      }),
                    );
                  }}
                  active={availableLanguage.id === language}
                />
              </li>
            ))}
          </ul>
        </Sheet>
      )}
      <Dialog
        dataQA="location-error-dialog"
        isOpen={isLocationErrorDialogOpen}
        title={t("location_error_dialog.title")}
        width={340}
        footer={
          <div className={styles.locationErrorDialogFooter}>
            <TextButton
              dataQA="location-error-dialog-cancel-button"
              text={t("location_error_dialog.cancel_button_text")}
              size="regular"
              type="primary"
              onClick={() => {
                setIsLocationErrorDialogOpen(false);
              }}
            />
            <Button
              dataQA="location-error-dialog-retry-button"
              text={t("location_error_dialog.retry_button_text")}
              size="regular"
              type="primary"
              style="filled"
              onClick={() => {
                setIsLocationErrorDialogOpen(false);
                handleLocationButtonClick();
              }}
            />
          </div>
        }
      >
        {locationErrorBody}
      </Dialog>
      <Dialog
        dataQA="map-attribution-dialog"
        isOpen={isMapAttributionOpen}
        closable={false}
        contextMenu={false}
        width={300}
        footer={
          <Button
            dataQA="map-attribution-dialog-ok-button"
            text="OK"
            size="regular"
            type="primary"
            style="filled"
            onClick={() => setIsMapAttributionOpen(false)}
          />
        }
      >
        <div
          className={styles.attributionDialog}
          dangerouslySetInnerHTML={{ __html: attributionHTML }}
        />
      </Dialog>
      {enableUI && isOnHomeScreen && Mobile && triggeredGeofence && (
        <Advert
          dataQA="geofence-advert"
          className={classNames(styles.advert, {
            [styles.extendedPadding]: searchTags,
          })}
          imageUrl={triggeredGeofence.image_url || ""}
          newTab={triggeredGeofence.new_tab}
          adUrl={triggeredGeofence.ad_url}
          onClose={() => dispatch(setTriggeredGeofenceId(null))}
          title={parseLanguageObject(triggeredGeofence.title, language) || ""}
          {...(triggeredGeofence.subtitle
            ? {
                subtitle:
                  parseLanguageObject(triggeredGeofence.subtitle, language) ||
                  "",
              }
            : {})}
          {...(triggeredGeofence.text
            ? {
                text:
                  parseLanguageObject(triggeredGeofence.text, language) || "",
              }
            : {})}
        />
      )}
      {enableUI &&
        Mobile &&
        isOnJourneyScreen &&
        selectedRouteGeofence &&
        selectedRouteGeofence}
    </div>
  );
};

export default Map;
